import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Calculator, DollarSign, Percent, Plus, UserPlus } from 'lucide-react';
import { type FormEvent, memo, useEffect, useState } from 'react';
import { Spinner } from '@/components/ui';
import { Button, SelectButtonsNew } from '@/components/ui';
import { ModalContentWrapper } from '@/components/ui';
import { useBorrowers, useCreateBorrower } from '@/hooks/use-borrowers';
import { calculateAmortization } from '@/lib/amortization';
import { api } from '@/lib/api';
import { cn } from '@/lib/cn';
import {
  LOAN_TYPE_DESCRIPTIONS,
  LOAN_TYPE_LABELS,
  getFrequencyLabel,
  getPrincipalHelperText,
  getPrincipalLabel,
  isLineOfCredit,
} from '@/lib/loan-type-labels';
import { toast } from '@/stores/toast-store';
import type { Loan, LoanType } from '@/types';

const LOAN_TYPES: { value: LoanType; label: string; desc: string }[] = [
  { value: 'PERSONAL', label: LOAN_TYPE_LABELS.PERSONAL, desc: LOAN_TYPE_DESCRIPTIONS.PERSONAL },
  { value: 'BUSINESS', label: LOAN_TYPE_LABELS.BUSINESS, desc: LOAN_TYPE_DESCRIPTIONS.BUSINESS },
  { value: 'MORTGAGE', label: LOAN_TYPE_LABELS.MORTGAGE, desc: LOAN_TYPE_DESCRIPTIONS.MORTGAGE },
  { value: 'AUTO', label: LOAN_TYPE_LABELS.AUTO, desc: LOAN_TYPE_DESCRIPTIONS.AUTO },
  {
    value: 'LINE_OF_CREDIT',
    label: LOAN_TYPE_LABELS.LINE_OF_CREDIT,
    desc: LOAN_TYPE_DESCRIPTIONS.LINE_OF_CREDIT,
  },
];

const FREQUENCIES = [
  { value: 'MONTHLY', label: 'Monthly', periods: 12 },
  { value: 'BIWEEKLY', label: 'Bi-weekly', periods: 26 },
  { value: 'WEEKLY', label: 'Weekly', periods: 52 },
  { value: 'QUARTERLY', label: 'Quarterly', periods: 4 },
];

const COMMON_RATES = [5, 6, 7, 8, 9, 10, 12, 15, 18, 20, 24];

const FREQUENCY_LABELS: Record<string, string> = {
  MONTHLY: 'Monthly',
  BIWEEKLY: 'Bi-weekly',
  WEEKLY: 'Weekly',
  QUARTERLY: 'Quarterly',
};

interface CreateLoanInput {
  borrowerId: string;
  loanType: LoanType;
  principal: number;
  interestRate: number;
  defaultRate?: number;
  termMonths: number;
  paymentFrequency: string;
  establishmentFee?: number;
  brokerageFee?: number;
  legalCosts?: number;
  legalCostsRecovered?: boolean;
  otherCosts?: number;
  otherCostsDescription?: string;
  interestMethod?: string;
  flatInterestAmount?: number;
  settlementDate?: string;
}

async function createLoan(input: CreateLoanInput): Promise<Loan> {
  const { data } = await api.post<Loan>('/loans', input);
  return data;
}

function fmt(num: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(num);
}

function fmtPrecise(num: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(num);
}

function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function normalizePhone(phone: string): string {
  return phone.replace(/[^\d+]/g, '');
}

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (loan: Loan) => void;
}

export const CreateLoanModal = memo(function CreateLoanModal({ open, onClose, onCreated }: Props) {
  const qc = useQueryClient();
  const { data: borrowerResult } = useBorrowers({ limit: 100 });
  const borrowers = borrowerResult?.data ?? [];
  const createBorrower = useCreateBorrower();

  // Form state
  const [borrowerId, setBorrowerId] = useState('');
  const [loanType, setLoanType] = useState<LoanType>('PERSONAL');
  const [principal, setPrincipal] = useState('');
  const [interestMethod, setInterestMethod] = useState<'PERCENTAGE' | 'FLAT_AMOUNT'>('PERCENTAGE');
  const [rateInput, setRateInput] = useState('');
  const [rateMode, setRateMode] = useState<'annual' | 'monthly'>('annual');
  const [flatInterestAmount, setFlatInterestAmount] = useState('');
  const [defaultRate, setDefaultRate] = useState('');
  const [defaultRateMode, setDefaultRateMode] = useState<'annual' | 'monthly'>('annual');
  const [termMonths, setTermMonths] = useState('');
  const [paymentFrequency, setPaymentFrequency] = useState('MONTHLY');
  const [settlementDate, setSettlementDate] = useState('');
  const [establishmentFee, setEstablishmentFee] = useState('');
  const [brokerageFee, setBrokerageFee] = useState('');
  const [legalCosts, setLegalCosts] = useState('');
  const [legalCostsRecovered, setLegalCostsRecovered] = useState(false);
  const [otherCosts, setOtherCosts] = useState('');
  const [otherCostsDescription, setOtherCostsDescription] = useState('');

  // Estimate view toggle
  const [estimateView, setEstimateView] = useState<'interest-only' | 'full'>('interest-only');

  // Quick add borrower state
  const [showNewBorrower, setShowNewBorrower] = useState(false);
  const [newBorrowerName, setNewBorrowerName] = useState('');
  const [newBorrowerEmail, setNewBorrowerEmail] = useState('');
  const [newBorrowerPhone, setNewBorrowerPhone] = useState('');
  const [quickBorrowerErrors, setQuickBorrowerErrors] = useState<{
    name?: string;
    email?: string;
    phone?: string;
  }>({});

  // Computed rates
  const annualRate =
    rateMode === 'annual' ? parseFloat(rateInput) || 0 : (parseFloat(rateInput) || 0) * 12;
  const monthlyRate =
    rateMode === 'monthly' ? parseFloat(rateInput) || 0 : (parseFloat(rateInput) || 0) / 12;

  const annualDefault =
    defaultRateMode === 'annual'
      ? parseFloat(defaultRate) || 0
      : (parseFloat(defaultRate) || 0) * 12;

  // Computed estimates
  const principalNum = parseFloat(principal) || 0;
  const termNum = parseInt(termMonths) || 0;
  const flatNum = parseFloat(flatInterestAmount) || 0;
  const isLoc = isLineOfCredit(loanType);

  useEffect(() => {
    if (isLoc && interestMethod !== 'PERCENTAGE') {
      setInterestMethod('PERCENTAGE');
      setFlatInterestAmount('');
    }
  }, [interestMethod, isLoc]);

  const monthlyPayment =
    interestMethod === 'FLAT_AMOUNT'
      ? (principalNum + flatNum) / Math.max(termNum, 1)
      : principalNum > 0 && annualRate > 0 && termNum > 0
        ? (() => {
            const r = annualRate / 100 / 12;
            const n = termNum;
            return (principalNum * r * Math.pow(1 + r, n)) / (Math.pow(1 + r, n) - 1);
          })()
        : 0;

  const totalFees =
    (parseFloat(establishmentFee) || 0) +
    (parseFloat(brokerageFee) || 0) +
    (parseFloat(legalCosts) || 0) +
    (parseFloat(otherCosts) || 0);

  const amortizationSummary =
    interestMethod === 'PERCENTAGE' && principalNum > 0 && annualRate > 0 && termNum > 0
      ? calculateAmortization({
          principal: principalNum,
          annualRate,
          termMonths: termNum,
          frequency: paymentFrequency as 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY' | 'QUARTERLY',
          repaymentType: 'STANDARD',
        })
      : null;

  const totalInterest =
    interestMethod === 'FLAT_AMOUNT'
      ? flatNum
      : (amortizationSummary?.totalInterest ?? monthlyPayment * termNum - principalNum);
  const interestOnlySummary =
    interestMethod === 'PERCENTAGE' && principalNum > 0 && annualRate > 0 && termNum > 0
      ? calculateAmortization({
          principal: principalNum,
          annualRate,
          termMonths: termNum,
          frequency: paymentFrequency as 'MONTHLY' | 'BIWEEKLY' | 'WEEKLY' | 'QUARTERLY',
          repaymentType: 'INTEREST_ONLY',
        })
      : null;
  const periodicInterestLabel = `${FREQUENCY_LABELS[paymentFrequency] ?? 'Periodic'} Interest`;
  const periodicInterestOnlyPayment = interestOnlySummary?.schedule[0]?.interest ?? 0;
  const interestOnlyTotalInterest = interestOnlySummary?.totalInterest ?? totalInterest;
  const periodicRepaymentLabel = `${FREQUENCY_LABELS[paymentFrequency] ?? 'Periodic'} Repayment`;
  const periodicRepayment = amortizationSummary?.schedule[0]?.payment ?? monthlyPayment;

  const mutation = useMutation({
    mutationFn: createLoan,
    onSuccess: loan => {
      qc.invalidateQueries({ queryKey: ['loans'] });
      qc.invalidateQueries({ queryKey: ['reports'] });
      qc.invalidateQueries({ queryKey: ['reporting'] });
      qc.invalidateQueries({ queryKey: ['activity', 'global'] });
      qc.invalidateQueries({ queryKey: ['risk-scorecards'] });
      qc.invalidateQueries({ queryKey: ['portfolio-risk-map'] });
      qc.invalidateQueries({ queryKey: ['concentration-risk'] });
      toast.success(`Loan ${loan.loanNumber} created`);
      onCreated?.(loan);
      onClose();
      resetForm();
    },
  });

  function resetForm() {
    setBorrowerId('');
    setLoanType('PERSONAL');
    setPrincipal('');
    setInterestMethod('PERCENTAGE');
    setRateInput('');
    setRateMode('annual');
    setFlatInterestAmount('');
    setDefaultRate('');
    setDefaultRateMode('annual');
    setTermMonths('');
    setPaymentFrequency('MONTHLY');
    setSettlementDate('');
    setEstablishmentFee('');
    setBrokerageFee('');
    setLegalCosts('');
    setLegalCostsRecovered(false);
    setOtherCosts('');
    setOtherCostsDescription('');
    setShowNewBorrower(false);
    setNewBorrowerName('');
    setNewBorrowerEmail('');
    setNewBorrowerPhone('');
    setQuickBorrowerErrors({});
  }

  function handleQuickAddBorrower() {
    const name = newBorrowerName.trim();
    const email = newBorrowerEmail.trim();
    const phone = newBorrowerPhone.trim();
    const normalizedPhone = normalizePhone(phone);

    const errors: {
      name?: string;
      email?: string;
      phone?: string;
    } = {};

    if (!name) {
      errors.name = 'Name is required';
    } else if (name.length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    if (email && !isValidEmail(email)) {
      errors.email = 'Enter a valid email address';
    }

    if (phone && normalizedPhone.replace(/\D/g, '').length < 8) {
      errors.phone = 'Phone number looks too short';
    }

    setQuickBorrowerErrors(errors);
    if (Object.keys(errors).length > 0) return;

    createBorrower.mutate(
      {
        name,
        email: email || undefined,
        phone: phone || undefined,
      },
      {
        onSuccess: (b: any) => {
          setBorrowerId(b.id);
          setShowNewBorrower(false);
          setNewBorrowerName('');
          setNewBorrowerEmail('');
          setNewBorrowerPhone('');
          setQuickBorrowerErrors({});
          toast.success(`Borrower "${b.name}" created`);
        },
        onError: (err: any) => {
          const status = err?.response?.status;
          const msg = err?.response?.data?.message || err?.message || '';
          if (status === 429) {
            toast.error('Too many requests — please wait a moment then try again');
          } else {
            toast.error(`Failed to create borrower${msg ? `: ${msg}` : ''}`);
          }
        },
      },
    );
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    // Guard against unreasonable rates (catches monthly↔annual confusion)
    if (interestMethod !== 'FLAT_AMOUNT' && annualRate > 100) {
      toast.error(
        `Annual rate of ${annualRate.toFixed(2)}% seems too high. Check monthly/annual toggle.`,
      );
      return;
    }
    mutation.mutate({
      borrowerId,
      loanType,
      principal: principalNum,
      interestRate: interestMethod === 'FLAT_AMOUNT' ? 0 : annualRate,
      defaultRate: annualDefault > 0 ? annualDefault : undefined,
      termMonths: termNum,
      paymentFrequency,
      settlementDate: settlementDate || undefined,
      establishmentFee: parseFloat(establishmentFee) || undefined,
      brokerageFee: parseFloat(brokerageFee) || undefined,
      legalCosts: parseFloat(legalCosts) || undefined,
      legalCostsRecovered,
      otherCosts: parseFloat(otherCosts) || undefined,
      otherCostsDescription: otherCostsDescription || undefined,
      interestMethod,
      flatInterestAmount: interestMethod === 'FLAT_AMOUNT' ? flatNum : undefined,
    });
  }

  if (!open) return null;

  const inputClass =
    'w-full rounded-md border border-gray-300 px-3 py-2 text-sm placeholder:text-gray-400 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 outline-none';

  return (
    <ModalContentWrapper open={open} onClose={onClose} title="New Loan Application">
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* ── Section 1: Borrower ──────────────────────── */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <UserPlus className="w-4 h-4 text-indigo-500" />
            Borrower
          </h3>
          <div className="flex gap-2">
            <select
              required
              value={borrowerId}
              onChange={e => setBorrowerId(e.target.value)}
              className={cn(inputClass, 'flex-1')}
            >
              <option value="">Select borrower…</option>
              {borrowers?.map(b => (
                <option key={b.id} value={b.id}>
                  {b.name} {b.email ? `(${b.email})` : ''}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setShowNewBorrower(!showNewBorrower)}
              className="flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 shrink-0"
            >
              <Plus className="w-3.5 h-3.5" />
              New
            </button>
          </div>

          {showNewBorrower && (
            <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-100 space-y-2">
              <p className="text-xs font-medium text-indigo-700">Quick Add Borrower</p>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <input
                    value={newBorrowerName}
                    onChange={e => {
                      setNewBorrowerName(e.target.value);
                      setQuickBorrowerErrors(prev => ({ ...prev, name: undefined }));
                    }}
                    placeholder="Name *"
                    className={cn(
                      'w-full rounded-md border px-2.5 py-1.5 text-xs bg-white',
                      quickBorrowerErrors.name ? 'border-red-300' : 'border-indigo-200',
                    )}
                  />
                  {quickBorrowerErrors.name && (
                    <p className="mt-1 text-[11px] text-red-600">{quickBorrowerErrors.name}</p>
                  )}
                </div>
                <div>
                  <input
                    value={newBorrowerEmail}
                    onChange={e => {
                      setNewBorrowerEmail(e.target.value);
                      setQuickBorrowerErrors(prev => ({ ...prev, email: undefined }));
                    }}
                    placeholder="Email"
                    type="email"
                    className={cn(
                      'w-full rounded-md border px-2.5 py-1.5 text-xs bg-white',
                      quickBorrowerErrors.email ? 'border-red-300' : 'border-indigo-200',
                    )}
                  />
                  {quickBorrowerErrors.email && (
                    <p className="mt-1 text-[11px] text-red-600">{quickBorrowerErrors.email}</p>
                  )}
                </div>
                <div>
                  <input
                    value={newBorrowerPhone}
                    onChange={e => {
                      setNewBorrowerPhone(e.target.value);
                      setQuickBorrowerErrors(prev => ({ ...prev, phone: undefined }));
                    }}
                    placeholder="Phone"
                    className={cn(
                      'w-full rounded-md border px-2.5 py-1.5 text-xs bg-white',
                      quickBorrowerErrors.phone ? 'border-red-300' : 'border-indigo-200',
                    )}
                  />
                  {quickBorrowerErrors.phone && (
                    <p className="mt-1 text-[11px] text-red-600">{quickBorrowerErrors.phone}</p>
                  )}
                </div>
              </div>
              <Button
                variant="primary"
                type="button"
                onClick={handleQuickAddBorrower}
                disabled={!newBorrowerName.trim() || createBorrower.isPending}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium disabled:!bg-gray-400"
              >
                {createBorrower.isPending ? (
                  <Spinner size="lg" className="w-3 h-3 animate-spin" />
                ) : (
                  <Plus className="w-3 h-3" />
                )}
                Add Borrower
              </Button>
            </div>
          )}
        </div>

        {/* ── Section 2: Loan Type ─────────────────────── */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Loan Type</h3>
          <div className="grid grid-cols-5 gap-2">
            {LOAN_TYPES.map(t => (
              <Button
                variant="secondary"
                key={t.value}
                type="button"
                onClick={() => setLoanType(t.value)}

                className={cn(
                  'p-2.5 rounded-lg border text-center transition-all w-full min-h-[72px]',
                  loanType === t.value
                    ? 'border-blue-500 bg-indigo-50 ring-1 ring-blue-500'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50',
                )}
              >
                <span className="flex flex-col items-center gap-0.5">
                  <span
                    className={cn(
                      'text-xs font-medium leading-tight',
                      loanType === t.value ? 'text-blue-700' : 'text-gray-700',
                    )}
                  >
                    {t.label}
                  </span>
                  <span className="text-[9px] leading-tight text-gray-400">{t.desc}</span>
                </span>
              </Button>
            ))}
          </div>
        </div>

        {/* ── Section 3: Loan Terms ────────────────────── */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-green-500" />
            Loan Terms
          </h3>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {getPrincipalLabel(loanType)}
              </label>
              <input
                type="number"
                required
                min="1"
                step="0.01"
                value={principal}
                onChange={e => setPrincipal(e.target.value)}
                placeholder="e.g. 250,000"
                className={inputClass}
              />
              {getPrincipalHelperText(loanType) && (
                <p className="mt-1 text-[10px] text-gray-500">{getPrincipalHelperText(loanType)}</p>
              )}
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Settlement / Start Date
              </label>
              <input
                type="date"
                value={settlementDate}
                onChange={e => setSettlementDate(e.target.value)}
                className={inputClass}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Term (months)</label>
              <input
                type="number"
                required
                min="1"
                value={termMonths}
                onChange={e => setTermMonths(e.target.value)}
                placeholder="36"
                className={inputClass}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                {getFrequencyLabel(loanType)}
              </label>
              <select
                value={paymentFrequency}
                onChange={e => setPaymentFrequency(e.target.value)}
                className={inputClass}
              >
                {FREQUENCIES.map(f => (
                  <option key={f.value} value={f.value}>
                    {f.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Interest Method Toggle */}
          {isLoc ? (
            <div className="mb-3 rounded-lg border border-blue-200 bg-blue-50 px-3 py-2 text-xs text-blue-800">
              Line of credit uses a percentage rate. Interest accrues on the drawn balance, not the
              full facility limit.
            </div>
          ) : (
            <div className="flex items-center gap-3 mb-2">
              <span className="text-xs font-medium text-gray-600">Interest Method:</span>
              <SelectButtonsNew
                value={interestMethod}
                onChange={setInterestMethod}
                options={[
                  {
                    value: 'PERCENTAGE',
                    label: (
                      <span className="flex items-center gap-1">
                        <Percent className="w-3 h-3" /> Rate
                      </span>
                    ),
                  },
                  {
                    value: 'FLAT_AMOUNT',
                    label: (
                      <span className="flex items-center gap-1">
                        <DollarSign className="w-3 h-3" /> Fixed Amount
                      </span>
                    ),
                  },
                ]}
                classes={{
                  container: 'w-auto',
                  buttonsContainer: 'min-h-0 bg-white border-gray-200',
                  button: 'px-3 py-1 text-xs font-medium',
                }}
                capitalize={false}
              />
            </div>
          )}

          {/* Interest Rate (percentage mode) or Flat Amount */}
          <div className="grid grid-cols-2 gap-4">
            {interestMethod === 'PERCENTAGE' ? (
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-medium text-gray-600 flex items-center gap-1">
                    <Percent className="w-3 h-3" />
                    Interest Rate
                  </label>
                  <SelectButtonsNew
                    value={rateMode}
                    onChange={setRateMode}
                    options={[
                      { value: 'annual', label: 'Annual' },
                      { value: 'monthly', label: 'Monthly' },
                    ]}
                    classes={{
                      container: 'w-auto',
                      buttonsContainer: 'min-h-0 bg-white border-gray-200',
                      button: 'px-2 py-0.5 text-[10px] font-medium',
                    }}
                    capitalize={false}
                  />
                </div>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={rateInput}
                  onChange={e => setRateInput(e.target.value)}
                  placeholder={rateMode === 'annual' ? 'e.g. 8.5' : 'e.g. 0.71'}
                  className={inputClass}
                />
                {rateInput && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    {rateMode === 'monthly'
                      ? `= ${annualRate.toFixed(2)}% p.a.`
                      : `= ${monthlyRate.toFixed(4)}% per month`}
                  </p>
                )}
                {/* Quick rate buttons */}
                <div className="flex flex-wrap gap-1 mt-2">
                  {COMMON_RATES.map(r => (
                    <Button
                      variant="secondary"
                      key={r}
                      type="button"
                      onClick={() => {
                        setRateInput(String(r));
                        setRateMode('annual');
                      }}
                      className={cn(
                        'px-1.5 py-0.5 rounded text-[10px] border transition-colors w-fit min-w-[97px]',
                        annualRate === r
                          ? 'border-blue-500 bg-indigo-50 text-blue-700'
                          : 'border-gray-200 text-gray-500 hover:bg-gray-50',
                      )}
                    >
                      {r}%
                    </Button>
                  ))}
                </div>
              </div>
            ) : (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <DollarSign className="w-3 h-3" />
                  Total Interest Amount ($)
                </label>
                <input
                  type="number"
                  required
                  min="0"
                  step="0.01"
                  value={flatInterestAmount}
                  onChange={e => setFlatInterestAmount(e.target.value)}
                  placeholder="e.g. 5000"
                  className={inputClass}
                />
                {flatNum > 0 && principalNum > 0 && termNum > 0 && (
                  <p className="text-[10px] text-gray-400 mt-1">
                    = {((flatNum / principalNum) * (12 / termNum) * 100).toFixed(2)}% p.a.
                    equivalent
                  </p>
                )}
              </div>
            )}

            {/* Default Rate */}
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-xs font-medium text-gray-600">Default Rate</label>
                <div className="flex rounded-md border border-gray-200 overflow-hidden">
                  <button
                    type="button"
                    onClick={() => setDefaultRateMode('annual')}
                    className={cn(
                      'px-2 py-0.5 text-[10px] font-medium transition-colors',
                      defaultRateMode === 'annual'
                        ? 'bg-red-600 text-white'
                        : 'text-gray-500 hover:bg-gray-50',
                    )}
                  >
                    Annual
                  </button>
                  <button
                    type="button"
                    onClick={() => setDefaultRateMode('monthly')}
                    className={cn(
                      'px-2 py-0.5 text-[10px] font-medium transition-colors',
                      defaultRateMode === 'monthly'
                        ? 'bg-red-600 text-white'
                        : 'text-gray-500 hover:bg-gray-50',
                    )}
                  >
                    Monthly
                  </button>
                </div>
              </div>
              <input
                type="number"
                min="0"
                step="0.01"
                value={defaultRate}
                onChange={e => setDefaultRate(e.target.value)}
                placeholder="Optional penalty rate"
                className={inputClass}
              />
              {defaultRate && (
                <p className="text-[10px] text-gray-400 mt-1">
                  {defaultRateMode === 'monthly'
                    ? `= ${annualDefault.toFixed(2)}% p.a.`
                    : `= ${(annualDefault / 12).toFixed(4)}% per month`}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* ── Section 4: Establishment Costs (always visible) ── */}
        <div>
          <h3 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
            <DollarSign className="w-4 h-4 text-amber-500" />
            Establishment Costs & Fees
          </h3>
          <div className="border border-gray-200 rounded-lg p-4 bg-gray-50 space-y-3">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Establishment Fee ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={establishmentFee}
                  onChange={e => setEstablishmentFee(e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Brokerage Fee ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={brokerageFee}
                  onChange={e => setBrokerageFee(e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Legal Costs ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={legalCosts}
                  onChange={e => setLegalCosts(e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-xs text-gray-700 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={legalCostsRecovered}
                    onChange={e => setLegalCostsRecovered(e.target.checked)}
                    className="rounded border-gray-300 text-indigo-600 focus:ring-blue-500"
                  />
                  Legal costs recovered from borrower
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">
                  Other Costs ($)
                </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={otherCosts}
                  onChange={e => setOtherCosts(e.target.value)}
                  placeholder="0.00"
                  className={inputClass}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Description</label>
                <input
                  value={otherCostsDescription}
                  onChange={e => setOtherCostsDescription(e.target.value)}
                  placeholder="e.g. Valuation fees"
                  className={inputClass}
                />
              </div>
            </div>
            {totalFees > 0 && (
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <span className="text-xs font-medium text-gray-600">Total Establishment Costs</span>
                <span className="text-sm font-semibold text-gray-900">{fmt(totalFees)}</span>
              </div>
            )}
          </div>
        </div>

        {/* ── Loan Summary (live calculator) ───────────── */}
        {principalNum > 0 &&
          termNum > 0 &&
          (interestMethod === 'FLAT_AMOUNT' ? flatNum > 0 : annualRate > 0) && (
            <div className="bg-indigo-50 rounded-lg border border-indigo-100 p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-xs font-semibold text-indigo-700 flex items-center gap-1.5">
                  <Calculator className="w-3.5 h-3.5" />
                  Loan Summary (Estimate)
                </h3>
                {!isLoc && (
                  <div className="flex rounded-md border border-indigo-200 overflow-hidden">
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => setEstimateView('interest-only')}
                      className={cn(
                        'px-2 py-0.5 text-[10px] font-medium transition-colors',
                        estimateView === 'interest-only'
                          ? 'bg-gray-600 text-white'
                          : 'text-indigo-500 hover:bg-indigo-100',
                      )}
                    >
                      Interest Only
                    </Button>
                    <Button
                      variant="secondary"
                      type="button"
                      onClick={() => setEstimateView('full')}
                      className={cn(
                        'px-2 py-0.5 text-[10px] font-medium transition-colors',
                        estimateView === 'full'
                          ? 'bg-gray-600 text-white'
                          : 'text-indigo-500 hover:bg-indigo-100',
                      )}
                    >
                      Full Breakdown
                    </Button>
                  </div>
                )}
              </div>

              {isLoc ? (
                <div className={`grid gap-3 ${totalFees > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                  <div>
                    <p className="text-[10px] text-indigo-500">Facility Limit</p>
                    <p className="text-sm font-semibold text-indigo-900">
                      {fmtPrecise(principalNum)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-indigo-500">Indicative Monthly Interest</p>
                    <p className="text-sm font-semibold text-indigo-900">
                      {fmtPrecise((principalNum * annualRate) / 100 / 12)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-indigo-500">Repayment Structure</p>
                    <p className="text-sm font-semibold text-indigo-900">
                      Interest-only, principal at maturity
                    </p>
                  </div>
                  {totalFees > 0 && (
                    <div>
                      <p className="text-[10px] text-indigo-500">Upfront Fees</p>
                      <p className="text-sm font-semibold text-indigo-900">
                        {fmtPrecise(totalFees)}
                      </p>
                    </div>
                  )}
                </div>
              ) : estimateView === 'interest-only' ? (
                <div className={`grid gap-3 ${totalFees > 0 ? 'grid-cols-4' : 'grid-cols-2'}`}>
                  <div>
                    <p className="text-[10px] text-indigo-500">{periodicInterestLabel}</p>
                    <p className="text-sm font-semibold text-indigo-900">
                      {fmtPrecise(periodicInterestOnlyPayment)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-indigo-500">Total Interest</p>
                    <p className="text-sm font-semibold text-indigo-900">
                      {fmtPrecise(interestOnlyTotalInterest)}
                    </p>
                  </div>
                  {totalFees > 0 && (
                    <>
                      <div>
                        <p className="text-[10px] text-indigo-500">Total Fees</p>
                        <p className="text-sm font-semibold text-indigo-900">
                          {fmtPrecise(totalFees)}
                        </p>
                      </div>
                      <div>
                        <p className="text-[10px] text-indigo-500">Cost to Borrower</p>
                        <p className="text-sm font-semibold text-indigo-900">
                          {fmtPrecise(interestOnlyTotalInterest + totalFees)}
                        </p>
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div className={`grid gap-3 ${totalFees > 0 ? 'grid-cols-4' : 'grid-cols-3'}`}>
                  <div>
                    <p className="text-[10px] text-indigo-500">{periodicRepaymentLabel}</p>
                    <p className="text-sm font-semibold text-indigo-900">
                      {fmtPrecise(periodicRepayment)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-indigo-500">Total Interest</p>
                    <p className="text-sm font-semibold text-indigo-900">
                      {fmtPrecise(totalInterest)}
                    </p>
                  </div>
                  <div>
                    <p className="text-[10px] text-indigo-500">Total Repayment</p>
                    <p className="text-sm font-semibold text-indigo-900">
                      {fmtPrecise(principalNum + totalInterest)}
                    </p>
                  </div>
                  {totalFees > 0 && (
                    <div>
                      <p className="text-[10px] text-indigo-500">Total Cost (incl. fees)</p>
                      <p className="text-sm font-semibold text-indigo-900">
                        {fmtPrecise(principalNum + totalInterest + totalFees)}
                      </p>
                    </div>
                  )}
                </div>
              )}
              {isLoc && (
                <p className="mt-3 text-[10px] text-indigo-600">
                  After creation, add drawdowns in the Tranches tab. The schedule will be generated
                  from the drawn balance only.
                </p>
              )}
            </div>
          )}

        {/* Error */}
        {mutation.isError && (
          <p className="text-sm text-red-600">
            {(mutation.error as any)?.response?.data?.message ?? 'Failed to create loan'}
          </p>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2 border-t border-gray-200">
          <Button variant="secondary" type="button" onClick={onClose} className="w-fit">
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"
            disabled={mutation.isPending}
            className="w-fit w-fit flex items-center gap-2 px-5 py-2 text-white rounded-md text-sm font-medium hover: disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {mutation.isPending && <Spinner size="lg" className="w-4 h-4 animate-spin" />}
            Create Loan Application
          </Button>
        </div>
      </form>
    </ModalContentWrapper>
  );
});
